export const BANNER_SELECTOR = '.pre-navigation-banner, .lessons-teacher-preview-bar'
export const BANNER_OFFSET_CSS_VAR = '--banner-offset'

// Full-width notification banners (e.g. security advisories, the teacher preview bar) render above
// the lesson container and push it down, but the container's height calc doesn't know about them.
// This measures their combined rendered height and exposes it as a CSS variable so the container
// height can subtract it. It re-measures when a banner is added/removed or resizes (expands/wraps),
// which pure-CSS :has() math can't handle since these banners aren't fixed-height. Returns a cleanup
// function that disconnects the observers and clears the variable.
export function observeBannerOffset(target: HTMLElement = document.documentElement): () => void {
  const updateOffset = () => {
    const banners = document.querySelectorAll<HTMLElement>(BANNER_SELECTOR)
    const totalHeight = Array.from(banners).reduce((sum, banner) => sum + banner.offsetHeight, 0)
    target.style.setProperty(BANNER_OFFSET_CSS_VAR, `${totalHeight}px`)
  }

  const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateOffset) : null

  const observeBanners = () => {
    resizeObserver?.disconnect()
    document.querySelectorAll<HTMLElement>(BANNER_SELECTOR).forEach(banner => resizeObserver?.observe(banner))
    updateOffset()
  }

  const bannerWasMutated = (mutations: MutationRecord[]) => mutations.some(mutation =>
    [...Array.from(mutation.addedNodes), ...Array.from(mutation.removedNodes)].some(node =>
      node instanceof HTMLElement && (node.matches(BANNER_SELECTOR) || !!node.querySelector(BANNER_SELECTOR))
    )
  )

  const mutationObserver = new MutationObserver(mutations => {
    if (bannerWasMutated(mutations)) { observeBanners() }
  })
  mutationObserver.observe(document.body, { childList: true, subtree: true })

  observeBanners()

  return () => {
    resizeObserver?.disconnect()
    mutationObserver.disconnect()
    target.style.removeProperty(BANNER_OFFSET_CSS_VAR)
  }
}
